import { tmdbService } from './tmdbApi';
import { tmdbRequestQueue } from './tmdbRequestQueue';
import { unifiedHttpsCallable } from '@watchlist/shared';

export interface ProcessedMovie {
  id: string;
  title: string;
  year?: number;
  posterPath?: string;
  overview?: string;
  genre_ids?: number[];
  vote_average?: number;
  release_date?: string;
  runtime?: number; // Added for full movie details
  Runtime?: string; // Added for compatibility with existing code
}

export interface ScreenshotProcessingResult {
  detectedMovies: ProcessedMovie[];
  detectionMethod: 'text' | 'visual' | 'hybrid';
  confidence: number;
  rawText?: string[];
  visualAnalysis?: string;
  processingTime: number;
}

export interface ScreenshotProcessingError {
  code: 'VISION_API_ERROR' | 'MOVIE_MATCHING_ERROR' | 'NETWORK_ERROR' | 'INVALID_IMAGE' | 'VISUAL_RECOGNITION_ERROR';
  message: string;
  details?: any;
}


class ScreenshotProcessingService {
  private readonly MAX_MOVIE_RESULTS = 100;

  /**
   * Process a screenshot image to detect movies using Gemini multimodal (single API call)
   */
  async processScreenshot(imageFile: File): Promise<ScreenshotProcessingResult> {
    const startTime = Date.now();

    try {
      this.validateImage(imageFile);

      const base64Image = await this.fileToBase64(imageFile);
      const mimeType = imageFile.type || 'image/jpeg';

      const visualMovieRecognition = unifiedHttpsCallable<
        { image: string; mimeType: string; maxMovies: number },
        { success: boolean; movies: string[]; confidence: number; detectionMethod: string }
      >('visualMovieRecognition', { timeout: 60_000, region: 'us-central1' });

      const result = await visualMovieRecognition({
        image: base64Image,
        mimeType,
        maxMovies: this.MAX_MOVIE_RESULTS,
      });

      const { movies: titles, confidence } = result.data;
      console.log(`🎬 Gemini extracted ${titles.length} titles from screenshot`);

      const detectedMovies = await this.matchMoviesWithTMDb(titles);
      const processingTime = Date.now() - startTime;

      return {
        detectedMovies,
        detectionMethod: 'visual',
        confidence: confidence ?? this.calculateOverallConfidence(detectedMovies),
        processingTime,
      };

    } catch (error) {
      throw this.handleProcessingError(error);
    }
  }

  /**
   * Validate image file before processing
   */
  private validateImage(imageFile: File): void {
    // Check file type
    if (!imageFile.type.startsWith('image/')) {
      throw new Error('Invalid file type. Please upload an image file.');
    }
    
    // Check file size (max 10MB)
    if (imageFile.size > 10 * 1024 * 1024) {
      throw new Error('Image file too large. Maximum size is 10MB.');
    }
    
    // Check for valid image extensions
    const validExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
    const fileName = imageFile.name.toLowerCase();
    const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));
    
    if (!hasValidExtension) {
      throw new Error('Invalid image format. Supported formats: JPG, PNG, GIF, WebP');
    }
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data URL prefix to get just the base64 data
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = () => reject(new Error('Failed to read image file'));
      reader.readAsDataURL(file);
    });
  }

  /**
   * Public method to search and process movies from a list of titles
   * Used by text input and link processing
   */
  async searchAndProcessMovies(movieTitles: string[]): Promise<ProcessedMovie[]> {
    console.log('🔍 Searching for movies:', movieTitles);
    
    const detectedMovies = await this.matchMoviesWithTMDb(movieTitles);
    
    console.log(`✅ Found ${detectedMovies.length} movies from ${movieTitles.length} titles`);
    
    return detectedMovies;
  }

  /**
   * Match potential titles with TMDb database
   * Enhanced with full movie details and smart duplicate handling
   */
  private async matchMoviesWithTMDb(potentialTitles: string[]): Promise<ProcessedMovie[]> {
    const detectedMovies: ProcessedMovie[] = [];
    const searchPromises: Promise<void>[] = [];
    
    // Limit concurrent searches to avoid rate limiting
    const batchSize = 5;
    
    for (let i = 0; i < potentialTitles.length && i < this.MAX_MOVIE_RESULTS; i += batchSize) {
      const batch = potentialTitles.slice(i, i + batchSize);
      
      const batchPromises = batch.map(async (title) => {
        try {
          const searchResults = await tmdbService.searchMovies(title, 1);
          
          if (searchResults.Search && searchResults.Search.length > 0) {
            // **FIX 1: Smart duplicate handling - prioritize by popularity and rating**
            const sortedResults = [...searchResults.Search].sort((a, b) => {
              // Primary sort: popularity (higher is better)
              const popularityDiff = (b.popularity || 0) - (a.popularity || 0);
              if (Math.abs(popularityDiff) > 1) { // Only use popularity if there's a significant difference
                return popularityDiff;
              }
              
              // Secondary sort: vote_average (higher is better) 
              const ratingDiff = (b.vote_average || 0) - (a.vote_average || 0);
              if (Math.abs(ratingDiff) > 0.5) { // Only use rating if there's a meaningful difference
                return ratingDiff;
              }
              
              // Tertiary sort: vote_count (more votes = more reliable)
              return (b.vote_count || 0) - (a.vote_count || 0);
            });
            
            // Take the best match (most popular/highest rated)
            const bestMatch = sortedResults[0];
            
            // Filter out adult content
            if (bestMatch.adult) {
              console.log(`Skipping adult movie: ${bestMatch.title}`);
              return; // Skip this movie
            }

            // **FIX 2: Get full movie details including runtime** (uses cache-first strategy)
            try {
              console.log(`🎬 Fetching full details for: ${bestMatch.title} (ID: ${bestMatch.id})`);
              const fullMovieDetails = await tmdbRequestQueue.getMovieData(bestMatch.id.toString());
              
              // Convert full movie details to our format
              const processedMovie: ProcessedMovie = {
                id: fullMovieDetails.id.toString(),
                title: fullMovieDetails.title || fullMovieDetails.Title || title,
                year: fullMovieDetails.Year ? parseInt(fullMovieDetails.Year) : 
                      (fullMovieDetails.release_date ? new Date(fullMovieDetails.release_date).getFullYear() : undefined),
                posterPath: fullMovieDetails.poster_path || fullMovieDetails.Poster || undefined,
                overview: fullMovieDetails.overview || fullMovieDetails.Plot,
                genre_ids: fullMovieDetails.genre_ids,
                vote_average: fullMovieDetails.vote_average,
                release_date: fullMovieDetails.release_date,
                // ✅ Runtime is now included from full movie details
                runtime: fullMovieDetails.runtime,
                Runtime: fullMovieDetails.Runtime, // Include both formats for compatibility
              };
              
              console.log(`✅ Successfully processed: ${processedMovie.title} (Runtime: ${processedMovie.Runtime || processedMovie.runtime || 'N/A'})`);
              detectedMovies.push(processedMovie);
              
            } catch (detailsError) {
              console.warn(`Failed to get full details for movie: "${bestMatch.title}"`, detailsError);
              
              // Fallback to basic movie data if full details fail
              const basicProcessedMovie: ProcessedMovie = {
                id: bestMatch.id.toString(),
                title: bestMatch.title,
                year: bestMatch.release_date ? new Date(bestMatch.release_date).getFullYear() : undefined,
                posterPath: bestMatch.poster_path || undefined,
                overview: bestMatch.overview,
                genre_ids: bestMatch.genre_ids,
                vote_average: bestMatch.vote_average,
                release_date: bestMatch.release_date,
                // No runtime provided for search results without full details
              };
              
              detectedMovies.push(basicProcessedMovie);
            }
          }
        } catch (error) {
          console.warn(`Failed to search for movie: "${title}"`, error);
          // Continue with other searches even if one fails
        }
      });
      
      searchPromises.push(...batchPromises);
      
      // Add delay between batches to respect rate limits
      if (i + batchSize < potentialTitles.length) {
        await new Promise(resolve => setTimeout(resolve, 200)); // Increased delay slightly for full movie fetches
      }
    }

    // Wait for all searches to complete
    await Promise.all(searchPromises);
    
    // Remove duplicates based on movie ID and limit results
    const uniqueMovies = detectedMovies.filter((movie, index, self) => 
      index === self.findIndex(m => m.id === movie.id)
    );
    
    const finalMovies = uniqueMovies.slice(0, this.MAX_MOVIE_RESULTS);
    console.log(`🎬 Movie matching complete: Found ${finalMovies.length} unique movies with full details`);
    return finalMovies;
  }

  /**
   * Calculate overall confidence score
   */
  private calculateOverallConfidence(movies: ProcessedMovie[]): number {
    if (movies.length === 0) return 0;
    
    // Base confidence on number of movies found and their popularity
    const baseConfidence = Math.min(movies.length / 10, 1) * 0.8;
    
    // Boost confidence for popular movies (high vote average)
    const popularityBoost = movies.reduce((sum, movie) => {
      return sum + (movie.vote_average || 0) / 10;
    }, 0) / movies.length * 0.2;
    
    return Math.min(baseConfidence + popularityBoost, 1);
  }

  private handleProcessingError(error: any): ScreenshotProcessingError {
    console.error('Screenshot processing error:', error);
    
    if (error.message?.includes('Vision API')) {
      return {
        code: 'VISION_API_ERROR',
        message: 'Failed to analyze image. Please try again.',
        details: error,
      };
    }
    
    if (error.message?.includes('Visual recognition')) {
      return {
        code: 'VISUAL_RECOGNITION_ERROR',
        message: 'Failed to perform visual movie recognition. Please try again.',
        details: error,
      };
    }
    
    if (error.message?.includes('network') || error.message?.includes('fetch')) {
      return {
        code: 'NETWORK_ERROR',
        message: 'Network error. Please check your connection and try again.',
        details: error,
      };
    }
    
    if (error.message?.includes('Invalid') || error.message?.includes('file')) {
      return {
        code: 'INVALID_IMAGE',
        message: error.message,
        details: error,
      };
    }
    
    return {
      code: 'MOVIE_MATCHING_ERROR',
      message: 'Failed to detect movies in the image. Please try a different image.',
      details: error,
    };
  }

}

export const screenshotProcessingService = new ScreenshotProcessingService();